import { useMemo } from 'react'
import { useWorkerHealth, type WorkerHealthRow } from '../../hooks/useCompetitiveData'

type HealthStatus = 'healthy' | 'degraded' | 'critical'

function formatLastRun(value: string | null): string {
  if (!value) {
    return 'Never'
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return 'Never'
  }

  return date.toLocaleString()
}

function formatAge(hoursSinceRun: number | null): string {
  if (hoursSinceRun === null || !Number.isFinite(hoursSinceRun)) {
    return 'Never'
  }

  if (hoursSinceRun < 24) {
    return `${Math.max(1, Math.round(hoursSinceRun))}h ago`
  }

  return `${Math.max(1, Math.round(hoursSinceRun / 24))}d ago`
}

function isWorkerOk(worker: WorkerHealthRow): boolean {
  const status = worker.status.toLowerCase()
  return !worker.is_stale && (status === 'ok' || status === 'success')
}

function getStatusBadge(worker: WorkerHealthRow): { label: string; className: string } {
  const status = worker.status.toLowerCase()
  if (status === 'error') {
    return {
      label: '✗ Error',
      className: 'border border-red-200 bg-red-50 text-red-700',
    }
  }
  if (worker.is_stale) {
    return {
      label: '⚠ Stale',
      className: 'border border-amber-200 bg-amber-50 text-amber-700',
    }
  }
  if (status === 'ok' || status === 'success') {
    return {
      label: '✓ OK',
      className: 'border border-emerald-200 bg-emerald-50 text-emerald-700',
    }
  }
  return {
    label: worker.status || 'Unknown',
    className: 'border border-slate-200 bg-slate-100 text-slate-700',
  }
}

function getBannerClasses(status: HealthStatus): string {
  if (status === 'critical') {
    return 'border-red-200 bg-red-50 text-red-800'
  }
  if (status === 'degraded') {
    return 'border-amber-200 bg-amber-50 text-amber-800'
  }
  return 'border-emerald-200 bg-emerald-50 text-emerald-800'
}

function getBannerLabel(status: HealthStatus): string {
  if (status === 'critical') {
    return 'Critical'
  }
  if (status === 'degraded') {
    return 'Degraded'
  }
  return 'Healthy'
}

export default function WorkerHealthPanel() {
  const { data, isLoading, error } = useWorkerHealth()

  const workers = data?.workers ?? []
  const overallStatus: HealthStatus = data?.overall_status ?? 'healthy'

  const okCount = useMemo(() => workers.filter((worker) => isWorkerOk(worker)).length, [workers])
  const errorMessage = error instanceof Error ? error.message : null

  return (
    <details className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <summary className="cursor-pointer list-none select-none px-5 py-4 text-sm font-semibold text-slate-800">
        🔧 Worker Health — {okCount} workers OK
      </summary>

      <div className="space-y-4 border-t border-slate-200 px-5 py-4">
        {isLoading ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
            Loading worker health...
          </div>
        ) : errorMessage ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{errorMessage}</div>
        ) : (
          <>
            <div className={`rounded-lg border px-3 py-2 text-sm font-semibold ${getBannerClasses(overallStatus)}`}>
              Overall Status: {getBannerLabel(overallStatus)} · {data?.stale_count ?? 0} stale
            </div>

            {workers.length === 0 ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                No worker runs found.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                      <th className="px-2 py-2 font-semibold">Worker</th>
                      <th className="px-2 py-2 font-semibold">Last Run</th>
                      <th className="px-2 py-2 font-semibold">Status</th>
                      <th className="px-2 py-2 font-semibold">Rows</th>
                      <th className="px-2 py-2 font-semibold">Age</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workers.map((worker) => {
                      const badge = getStatusBadge(worker)
                      return (
                        <tr key={worker.worker_name} className="border-b border-slate-100 last:border-0">
                          <td className="px-2 py-2 font-medium text-slate-900">{worker.worker_name}</td>
                          <td className="px-2 py-2 text-slate-700">{formatLastRun(worker.last_run)}</td>
                          <td className="px-2 py-2">
                            <span className={`inline-flex rounded-md px-2 py-1 text-xs font-semibold ${badge.className}`}>
                              {badge.label}
                            </span>
                          </td>
                          <td className="px-2 py-2 text-slate-700">{Number(worker.rows_affected ?? 0)}</td>
                          <td className="px-2 py-2 text-slate-700">{formatAge(worker.hours_since_run)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </details>
  )
}
